import { ExtrudeGeometry, Shape, ShapeGeometry, type BufferGeometry } from 'three';
import type { ProfilePoint, VehicleSpec } from './types';

const BEVEL = 0.05;

function shapeFrom(points: readonly ProfilePoint[]): Shape {
  const shape = new Shape();
  points.forEach(([u, v], i) => (i === 0 ? shape.moveTo(u, v) : shape.lineTo(u, v)));
  shape.closePath();
  return shape;
}

/**
 * Profile (u along the length, v up) → vehicle space: x across, y up, z = L/2 - u, so the nose points to -z.
 */
export function bodyGeometry(spec: VehicleSpec): BufferGeometry {
  const depth = spec.width - BEVEL * 2;
  const geometry = new ExtrudeGeometry(shapeFrom(spec.profile), {
    depth,
    bevelEnabled: true,
    bevelThickness: BEVEL,
    bevelSize: BEVEL * 0.6,
    bevelSegments: 2,
  });
  geometry.rotateY(Math.PI / 2);
  geometry.translate(-depth / 2, 0, spec.length / 2);
  return geometry;
}

/** Flat side panel matching the silhouette, for the painted livery. */
export function sideGeometry(spec: VehicleSpec, facing: 'right' | 'left'): BufferGeometry {
  const offset = spec.width / 2 + 0.006;
  if (facing === 'right') {
    const geometry = new ShapeGeometry(shapeFrom(spec.profile));
    geometry.rotateY(Math.PI / 2);
    geometry.translate(offset, 0, spec.length / 2);
    return geometry;
  }
  const mirrored = spec.profile.map(([u, v]) => [spec.length - u, v] as const).reverse();
  const geometry = new ShapeGeometry(shapeFrom(mirrored));
  geometry.rotateY(-Math.PI / 2);
  geometry.translate(-offset, 0, -spec.length / 2);
  return geometry;
}
